import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { SupabaseClientService } from '@services/db/supabase-client';
import { ToasterService } from '@services/toaster.service';
import { environment } from '@app/environment';

/**
 * Configuration for Supabase proxy routes
 */
export interface SupabaseProxyRoute {
  /** Base URL to match and proxy */
  baseUrl: string;
  /** Supabase Edge Function name */
  functionName: string;
}

/**
 * Supabase Proxy Interceptor
 *
 * Automatically intercepts HTTP requests matching configured routes
 * and proxies them through Supabase Edge Functions with JWT authentication
 *
 * Features:
 * - Automatic JWT token injection
 * - URL rewriting for Edge Function
 * - Transparent proxying
 * - No code changes needed in services
 * - Blocks anonymous users (requires authentication)
 *
 * Usage in app.config.ts:
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideHttpClient(
 *       withInterceptors([supabaseProxyInterceptor])
 *     )
 *   ]
 * };
 * ```
 */
export const supabaseProxyInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const supabase = inject(SupabaseClientService);
  const toaster = inject(ToasterService);

  // Check if this request should be proxied
  const route = findMatchingRoute(req.url);

  if (!route) {
    // No proxy needed - pass through
    return next(req);
  }

  // Check if user is authenticated before proxying
  return from(checkAuthentication(supabase)).pipe(
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        // User is not authenticated - block the request
        console.warn('[Supabase Proxy Interceptor] Blocking request for anonymous user:', req.url);
        toaster.error('Authentication Required', 'Please sign in to access this resource.');
        return throwError(() => new Error('Authentication required. Please sign in to access this resource.'));
      }

      // User is authenticated - proceed with proxy
      return from(getAuthToken(supabase)).pipe(
        switchMap(token => {
          const proxyReq = buildProxyRequest(req, route, token);
          return next(proxyReq);
        })
      );
    }),
    catchError(error => {
      console.error('[Supabase Proxy Interceptor] Error:', error);

      // Handle HTTP error responses from the proxy
      if (error instanceof HttpErrorResponse) {
        const errorBody = error.error;

        // Check for specific error types
        if (error.status === 401) {
          toaster.error('Unauthorized', errorBody?.message || 'Invalid or expired authentication token.');
        } else if (error.status === 403) {
          toaster.error('Access Denied', errorBody?.message || 'You do not have permission to access this resource.');
        } else if (error.status >= 500) {
          toaster.error('Server Error', 'An error occurred while processing your request. Please try again later.');
        } else {
          toaster.error('Error', errorBody?.message || 'An unexpected error occurred.');
        }
      } else {
        // Non-HTTP error
        toaster.error('Error', error.message || 'An unexpected error occurred.');
      }

      return throwError(() => error);
    })
  );
};

/**
 * Find matching proxy route for given URL
 */
function findMatchingRoute(url: string): SupabaseProxyRoute | null {
  const routes = environment.supabase.proxyRoutes || [];

  for (const route of routes) {
    if (url.startsWith(route.baseUrl)) {
      return route;
    }
  }
  return null;
}

/**
 * Check if user is authenticated
 */
async function checkAuthentication(supabase: SupabaseClientService): Promise<boolean> {
  try {
    const session = await supabase.client.auth.getSession();
    return !!session.data.session?.access_token;
  } catch (error) {
    console.error('[Supabase Proxy Interceptor] Error checking authentication:', error);
    return false;
  }
}

/**
 * Get JWT token from Supabase session
 */
async function getAuthToken(supabase: SupabaseClientService): Promise<string> {
  const session = await supabase.client.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) {
    throw new Error('User is not authenticated. Please sign in.');
  }

  return token;
}

/**
 * Build proxied request for Supabase Edge Function
 */
function buildProxyRequest(
  originalReq: HttpRequest<unknown>,
  route: SupabaseProxyRoute,
  token: string
): HttpRequest<unknown> {
  // Extract path from original URL
  const path = extractPath(originalReq.url, route.baseUrl);

  // Build Edge Function URL
          const edgeFunctionUrl = `${environment.supabase.url}/functions/v1/${route.functionName}`;

  // Build query params including the 'path' parameter
  const params = buildProxyParams(originalReq, path);

  // Clone request with new URL and auth header
  return originalReq.clone({
    url: edgeFunctionUrl,
    setParams: params,
    setHeaders: {
      'Authorization': `Bearer ${token}`,
      'apikey': environment.supabase.publicKey,
      'Content-Type': 'application/json'
    }
  });
}

/**
 * Extract path from full URL relative to upstream base
 */
function extractPath(url: string, baseUrl: string): string {
  try {
    const urlObj = new URL(url);

    // Check if URL starts with base URL
    if (url.startsWith(baseUrl)) {
      // Extract everything after the base URL
      return url.substring(baseUrl.length);
    }

    // Return path + search
    return urlObj.pathname + urlObj.search;
  } catch {
    // If URL parsing fails, return as-is
    return url;
  }
}

/**
 * Build query parameters for Edge Function
 * Includes 'path' parameter and preserves original query params
 */
function buildProxyParams(
  req: HttpRequest<unknown>,
  path: string
): { [param: string]: string } {
  const params: { [param: string]: string } = {
    path: path
  };

  // Preserve original query parameters
  req.params.keys().forEach(key => {
    const values = req.params.getAll(key);
    if (values && values.length > 0) {
      // Join multiple values with comma or use first value
      params[key] = values.length === 1 ? values[0] : values.join(',');
    }
  });

  return params;
}

